import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AuthService } from './../auth/auth.service';
import { Constant } from './../constant';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { MyEvent, MyEventApproval } from '../model/my-event';
import { MyEventCreateDialogComponent } from '../dialog/my-event-create-dialog/my-event-create-dialog.component';
import { Auth } from '@aws-amplify/auth';

@Component({
  selector: 'app-my-event',
  templateUrl: './my-event.component.html',
  styleUrl: './my-event.component.css',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDialogModule,
    MatProgressSpinnerModule,
  ]
})
export class MyEventComponent implements OnInit, OnDestroy {
  public apiPath = '/myevent';
  public readonly Constant = Constant;
  public showSpinner = false;

  // タブ
  public activeTab: 'draft' | 'approval' = 'draft';

  // フィルター（完了／対応済のイベントも表示するか）
  public showCompleted = false;

  // 承認依頼通知
  public hasApprovalRequest = false;

  // 起票イベントリスト
  public draftEvents: MyEvent[] = [];

  // 承認イベントリスト
  public approvalEvents: MyEventApproval[] = [];

  // ページング
  public page = 1;
  public totalCount = 0;
  public readonly pageSize = Constant.myEventPageSize;

  // 一覧取得リクエストの連番（タブ切替などで発生した古いレスポンスを破棄するために使用）
  private requestSeq = 0;

  private routeSub: Subscription;

  //targetSelectedNodes: any[] = [];

  private orgMasterData: any = {};

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleMyEvent + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  ngOnInit() {
    window.scroll(0, 0);

    // URLパスに基づいてタブを決定
    this.routeSub = this.route.url.subscribe(segments => {
      const path = segments.map(s => s.path).join('/');
      this.page = 1;
      this.activeTab = path.includes('approval') ? 'approval' : 'draft';
      this.loadEvents();
    });
  }

  ngOnDestroy() {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  // タブ切り替え
  onTabChange(tab: 'draft' | 'approval') {
    if (tab === this.activeTab) return;

    if (tab === 'approval') {
      this.router.navigate(['/myevent/approval']);
    } else {
      this.router.navigate(['/myevent']);
    }
  }

  // 完了/対応済イベント表示切り替え（サーバから取得し直す）
  onShowCompletedChange() {
    this.page = 1;
    this.loadEvents();
  }

  // ページング
  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get pages(): number[] {
    const maxSize = 11;
    const total = this.totalPages;

    if (total <= maxSize) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    let start = Math.max(1, this.page - Math.floor(maxSize / 2));
    let end = start + maxSize - 1;

    if (end > total) {
      end = total;
      start = end - maxSize + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  isFirstPage(): boolean {
    return this.page === 1;
  }

  isLastPage(): boolean {
    return this.page >= this.totalPages;
  }

  goPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }

    this.page = page;

    window.scroll(0, 0);
    this.loadEvents();
  }

  previous(): void {
    this.goPage(this.page - 1);
  }

  next(): void {
    this.goPage(this.page + 1);
  }

  // 新規作成ダイアログ
  onCreate() {
    if (!this.commonUtil.checkOnline()) {
      return;
    }
    const dialogRef = this.dialog.open(MyEventCreateDialogComponent, {
      width: '600px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {

        this.router.navigate(
          ['/myevent-detail', result.eventId],
          {
            queryParams: {
              edit: 'true'
            }
          }
        );
        this.page = 1;
        this.loadDraftEvents();
      }
    });
  }

  // イベント詳細へ遷移（起票タブ）
  onEventClick(event: MyEvent) {
    this.router.navigate(['/myevent-detail', event.id],
      { queryParams: { from: 'draft' }});
  }

  // イベント詳細へ遷移（承認タブ）
  onApprovalEventClick(event: MyEventApproval) {
    this.router.navigate(['/myevent-detail', event.id],
      { queryParams: { from: 'approval' } });
  }

  // カバー率計算（起票タブ）
  getCoverageRate(event: MyEvent): string {
    if (!event.participantCount || !event.totalMembers || event.totalMembers === 0) {
      return '- %';
    }
    const rate = Math.round((event.participantCount / event.totalMembers) * 100);
    return `${rate}%`;
  }

  // カバー率計算（承認タブ）
  getApprovalCoverageRate(event: MyEventApproval): string {
    if (!event.participantCount || !event.totalMembers || event.totalMembers === 0) {
      return '- %';
    }
    const rate = Math.round((event.participantCount / event.totalMembers) * 100);
    return `${rate}%`;
  }

  // 概要の省略表示（121文字以上で省略）
  getTruncatedDescription(description: string): string {
    if (!description) return '';
    if (description.length <= 121) return description.replace(/\n/g, ' ');
    return description.replace(/\n/g, ' ').substring(0, 121) + '...';
  }

  // 開催日の表示（最大3つ + など）
  getDisplayDates(event: MyEvent | MyEventApproval): string[] {

    // schedules がある場合
    if (event.schedules?.length) {

      const uniqueDates = [
        ...new Set(event.schedules.map(s => s.eventDate))
      ].sort();

      // 期間表示
      if (event.numberOfDays === 'period') {

        const start = uniqueDates[0];
        const end = uniqueDates[uniqueDates.length - 1];

        return [
          `${this.getFormattedDate(start)}\n〜\n${this.getFormattedDate(end)}`
        ];
      }

      // 通常表示（最大3件）
      return uniqueDates
        .slice(0, 3)
        .map(date => this.getFormattedDate(date));
    }

    // eventDates fallback
    if (event.eventDates?.length) {

      if (event.numberOfDays === 'period') {

        return [
          `${this.getFormattedDate(event.eventDates[0])}\n〜\n${
            this.getFormattedDate(
              event.eventDates[event.eventDates.length - 1]
            )
          }`
        ];
      }

      return event.eventDates
        .slice(0, 3)
        .map(date => this.getFormattedDate(date));
    }

    return [];
  }

  // 帳票種類の表示
  getDocumentTypeLabel(type: 'plan' | 'report'): string {
    return Constant.myEventDocumentTypes[type];
  }

  // ステータスの表示
  getStatusLabel(status: 'draft' | 'reviewing' | 'completed'): string {
    return Constant.myEventStatuses[status];
  }

  // 承認ステータスの表示
  getApprovalStatusLabel(status: string): string {
    return Constant.myEventApprovalStatuses[status] || status;
  }

  // チェックボックスのラベルを取得
  getCheckboxLabel(): string {
    return this.activeTab === 'draft' ? '完了したイベントを表示' : '未着手/対応済のイベントを表示';
  }

  getFormattedDate(date: string): string {
    if (!date) return '';
    return date.replace(/-/g, '/');
  }

  // 表示中のタブの一覧を取得する
  private loadEvents() {
    if (this.activeTab === 'approval') {
      this.loadApprovalEvents();
    } else {
      this.loadDraftEvents();
    }
  }

  // 一覧取得APIのクエリ文字列
  private buildQuery(): string {
    return (
      `?page=${this.page - 1}` +
      `&size=${this.pageSize}` +
      `&showCompleted=${this.showCompleted ? 1 : 0}`
    );
  }

  // 一覧取得APIの共通処理
  // タブ切替やフィルター変更でリクエストが重なった場合、最後のリクエストの結果のみを反映する
  private fetchEvents(apiPath: string, onSuccess: (data: any) => void) {
    if (!this.commonUtil.isOnline()) {
      this.showSpinner = false;
      return;
    }

    const seq = ++this.requestSeq;

    // ぐるぐる表示
    this.showSpinner = true;

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);

      this.commonUtil
        .apiGet(apiPath + this.buildQuery(), options)
        .then(res => {

          // 後から新しいリクエストが開始されている場合は、古いレスポンスを破棄する
          if (seq !== this.requestSeq) {
            return;
          }

          onSuccess(res.data);

          this.showSpinner = false;
        })
        .catch(err => {
          this.commonUtil.debug().log(err);

          if (seq !== this.requestSeq) {
            return;
          }

          if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.showSpinner = false;
            this.toastUtil.showErrorToast(
              '',
              Constant.msgNetworkError,
              Constant.toastShowMiliSec
            );
          }
        });
    });
  }

  private loadApprovalEvents() {
    this.fetchEvents(this.apiPath + '/approval', data => {
      this.approvalEvents = data.items;
      this.totalCount = data.total;

      // 承認依頼有無
      this.hasApprovalRequest = data.actionRequiredTotal > 0;

      this.commonUtil.debug().log(this.approvalEvents);
    });
  }

  private loadDraftEvents() {
    this.fetchEvents(this.apiPath + '/draft', data => {
      this.draftEvents = data.items;
      this.totalCount = data.total;

      // 承認依頼有無
      this.hasApprovalRequest = data.actionRequiredTotal > 0;

      this.commonUtil.debug().log(this.draftEvents);
    });
  }

  getMasterTypeName(master_type: number): string {
    switch(master_type) {
      case 1: return '本部';
      case 2: return '支部';
      case 3: return 'エリア';
      case 4: return '専門部';
      default: return '';
    }
  }

  getDepartmentFromOrganizations(event: MyEventApproval): any {
    const orgs = [
      ...(event.drafter?.organizations ?? []),
      ...(event.drafter?.concurrent_organizations ?? [])
    ];

    if (!orgs || orgs.length === 0) return null;

    return orgs[0]; // ← 一番最初（例: 0,1,2,3,4 の 0）
  }

  private loadOfficialPositions(options: object): Promise<void> {
    return this.commonUtil.apiGet('/master', options).then(res => {
      const master = res.data?.response?.data?.master ?? {};
      this.orgMasterData = master;
    });
  }

  private buildSelectedNodesFromIds(ids: number[]): any[] {
    if (!ids || ids.length === 0) return [];

    const idSet = new Set(ids);
    const result: any[] = [];

    // #skipを透過して最初の非skipレベルの人数を合計する
    // - #skipノードは透過して子を再帰探索
    // - 非skipノードに到達したらmember_countを取得し、それより下は見ない
    const sumChushinCount = (nodes: any[]): number =>
      nodes.reduce((sum: number, n: any) => {
        if (n.name?.includes('#skip')) return sum + sumChushinCount(n.children ?? []);
        return sum + (n.member_count || 0);
      }, 0);

    // 全体（先頭固定）a
    if (idSet.has(-1)) {
      const zenbuCount = ['1', '2', '3', '4'].reduce((total, key) => {
        return total + sumChushinCount(this.orgMasterData[key] ?? []);
      }, 0);
      result.push({ id: -1, name: '全体', path: '全体', master_type: 0, org_count: zenbuCount, orgCountForCoverage: zenbuCount });
    }

    type Entry = { id: number; name: string; memberCount: number | null; masterType: number; ancestorIds: number[]; isSkip: boolean; isVirtualRoot: boolean };
    const allEntries: Entry[] = [];

    const virtualRoots = [
      { id: 1, name: '本部',  masterType: 1, key: '1' },
      { id: 2, name: '支部',  masterType: 2, key: '2' },
      { id: 3, name: 'エリア', masterType: 3, key: '3' },
      { id: 4, name: '専門部', masterType: 4, key: '4' },
    ];

    for (const vr of virtualRoots) {
      allEntries.push({ id: vr.id, name: vr.name, memberCount: null, masterType: vr.masterType, ancestorIds: [], isSkip: false, isVirtualRoot: true });

      const traverse = (nodes: any[], ancestorIds: number[]) => {
        for (const n of nodes) {
          const isSkip = n.name?.includes('#skip');
          allEntries.push({ id: n.id, name: n.name, memberCount: n.member_count ?? null, masterType: vr.masterType, ancestorIds: [...ancestorIds], isSkip, isVirtualRoot: false });
          if (n.children) traverse(n.children, [...ancestorIds, n.id]);
        }
      };
      traverse(this.orgMasterData[vr.key] ?? [], [vr.id]);
    }

    const selectedEntries = allEntries.filter(e => idSet.has(e.id));
    const selectedIdSet = new Set(selectedEntries.map(e => e.id));

    for (const e of selectedEntries) {
      // 大分類（仮想ルート）が選択済みの場合、その下の中分類は二重カウントしない
      const hasCheckedParentWithOrgCount = e.ancestorIds.some(aid => {
        if (!selectedIdSet.has(aid)) return false;
        const anc = allEntries.find(a => a.id === aid);
        return anc != null && (anc.memberCount != null || anc.isVirtualRoot);
      });

      let orgCountForCoverage: number;
      if (hasCheckedParentWithOrgCount) {
        orgCountForCoverage = 0;
      } else if (e.isVirtualRoot) {
        // 大分類が選択された場合: チェックの有無にかかわらず全中分類の人数を合計
        orgCountForCoverage = sumChushinCount(this.orgMasterData[String(e.masterType)] ?? []);
      } else {
        orgCountForCoverage = e.memberCount || 0;
      }

      const pathParts: string[] = [];
      for (const aid of e.ancestorIds) {
        const anc = allEntries.find(a => a.id === aid);
        if (anc && !anc.isSkip) pathParts.push(anc.name);
      }
      if (!e.isSkip) pathParts.push(e.name);

      result.push({ id: e.id, name: e.name, org_count: e.memberCount, master_type: e.masterType, path: pathParts.join(' '), orgCountForCoverage });
    }

    // 他組織（末尾固定）
    if (idSet.has(-2)) result.push({ id: -2, name: '他組織', path: '他組織', master_type: 0, org_count: null, orgCountForCoverage: 0 });


    //this.showSpinner = false;
    return result;
  }

  hasMoreDates(event: MyEvent | MyEventApproval): boolean {
    // schedules優先
    if (event.schedules?.length) {
      const uniqueDates = [
        ...new Set(event.schedules.map(s => s.eventDate))
      ];

      return uniqueDates.length > 3;
    }

    // eventDates
    return (event.eventDates?.length || 0) > 3;
  }

}
